import React, { useState } from 'react';
import { apiUrl } from '../services/api';

/**
 * Category + Type select with "Add new type" option.
 * When user adds a type, calls onTypeCreated(newType) so parent can refetch and set the new type.
 */
export default function ActivityTypeSelect({
  categories = [],
  types = [],
  categoryId,
  typeId,
  onCategoryChange,
  onTypeChange,
  onTypeCreated,
  placeholderCategory = 'Select Category',
  placeholderType = 'Select Type',
  disabled = false,
  showCategory = true,
  valueKey = 'type_id',
}) {
  const [isAdding, setIsAdding] = useState(false);
  const [newTypeName, setNewTypeName] = useState('');
  const [addError, setAddError] = useState('');

  const filteredTypes = (types || []).filter(t => String(t.category_id) === String(categoryId));
  const catId = String(categoryId || '');
  const useNameAsValue = valueKey === 'type_name';

  const handleAddType = async () => {
    const name = (newTypeName || '').trim();
    if (!name) {
      setAddError('Enter a type name');
      return;
    }
    if (!catId) {
      setAddError('Select a category first');
      return;
    }
    setAddError('');
    try {
      const res = await fetch(apiUrl('/api/activity_types'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ category_id: catId, type_name: name }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || `Request failed (${res.status})`);
      }
      setNewTypeName('');
      setIsAdding(false);
      onTypeCreated?.(data);
    } catch (err) {
      setAddError(err.message || 'Failed to add type');
    }
  };

  return (
    <>
      {showCategory && (
        <div className="tp-field">
          <span className="tp-label">Category</span>
          <select
            value={catId}
            onChange={e => {
              onCategoryChange?.(e.target.value);
              onTypeChange?.('');
              setIsAdding(false);
              setNewTypeName('');
            }}
            disabled={disabled}
          >
            <option value="">{placeholderCategory}</option>
            {(categories || []).map(c => (
              <option key={c.category_id} value={String(c.category_id)}>{c.category_name}</option>
            ))}
          </select>
        </div>
      )}

      <div className="tp-field">
        <span className="tp-label">Type</span>
        <select
          value={String(typeId || '')}
          onChange={e => onTypeChange?.(e.target.value)}
          disabled={!catId || disabled}
        >
          <option value="">{placeholderType}</option>
          {filteredTypes.map(t => (
            <option
              key={t.type_id}
              value={useNameAsValue ? String(t.type_name || '') : String(t.type_id)}
            >
              {t.type_name}
            </option>
          ))}
        </select>
        {catId && !isAdding && (
          <button
            type="button"
            className="tp-btn--ghost"
            style={{ marginTop: 6, fontSize: 13 }}
            onClick={() => setIsAdding(true)}
          >
            + Add new type…
          </button>
        )}

        {isAdding && (
          <div style={{ marginTop: 8, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <input
              type="text"
              placeholder="Type name"
              value={newTypeName}
              onChange={e => setNewTypeName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), handleAddType())}
              style={{ flex: 1, minWidth: 120 }}
              autoFocus
            />
            <button type="button" onClick={handleAddType}>
              Add
            </button>
            <button type="button" className="tp-btn--ghost" onClick={() => setIsAdding(false)}>
              Cancel
            </button>
            {addError && <span style={{ color: 'var(--tp-danger, #c53030)', fontSize: 13 }}>{addError}</span>}
          </div>
        )}
      </div>
    </>
  );
}
